import React from 'react';
import Modal from '@hero/ui-kit/components/Modal';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import Button from '@hero/ui-kit/inputs/Button';
import Section from '@hero/ui-kit/layout/Section';
import DatePicker from '@hero/ui-kit/components/DatePicker';
import usePostponeSla from '../../api/usePostponeSla';
import * as Style from './style.module.scss';

// HWM-22633 (round 2): redesigned per `sla_modal_design.png`. Title "Postpone
// SLA Timer", two pill-shaped pickers (DATE + TIME), New note textarea, and a
// single orange "Update SLA Timer" button — Modal X handles dismissal.

interface PostponeSlaModalProps {
    leadId: string;
    // Retained for backwards-compat with the existing caller; not rendered in
    // the new design.
    patientName?: string;
    currentSlaText?: string | null;
    onClose: () => void;
}

const PostponeSlaModal: React.FC<PostponeSlaModalProps> = ({ leadId, onClose }) => {
    const [selectedDate, setSelectedDate] = React.useState<Date | null>(null);
    const [selectedTime, setSelectedTime] = React.useState<Date | null>(null);
    const [reason, setReason] = React.useState('');

    const { mutateAsync: postponeSla, isPending } = usePostponeSla(onClose);

    const tomorrow = React.useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() + 1);
        return d;
    }, []);

    const combinedDateTime = React.useMemo(() => {
        if (!selectedDate) return null;
        const d = new Date(selectedDate);
        if (selectedTime) {
            d.setHours(selectedTime.getHours(), selectedTime.getMinutes(), 0, 0);
        } else {
            d.setHours(10, 0, 0, 0);
        }
        return d;
    }, [selectedDate, selectedTime]);

    const canSubmit = !!selectedDate && !isPending;

    const handleSubmit = async () => {
        if (!combinedDateTime) return;
        await postponeSla({
            payload: {
                lead_id: leadId,
                postpone_until: combinedDateTime.toISOString(),
                reason: reason.trim() || undefined
            }
        });
    };

    return (
        <Modal isCancelable onClose={onClose} className={Style.modalSmall}>
            <div className={Style.modalBody}>
                <H role="h4" centered noDefaultMargin>
                    Postpone SLA Timer
                </H>

                <div className={Style.fieldGroup}>
                    <P noDefaultMargin className={Style.uppercaseLabel}>
                        Date
                    </P>
                    <div className={Style.pillPicker}>
                        <DatePicker
                            selected={selectedDate}
                            onChange={(d: Date | null) => setSelectedDate(d)}
                            minDate={tomorrow}
                            dateFormat="MM/dd/yyyy"
                            placeholderText="MM/DD/YYYY"
                        />
                    </div>
                </div>

                <div className={Style.fieldGroup}>
                    <P noDefaultMargin className={Style.uppercaseLabel}>
                        Time
                    </P>
                    <div className={Style.pillPicker}>
                        <DatePicker
                            selected={selectedTime}
                            onChange={(d: Date | null) => setSelectedTime(d)}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption="Time"
                            dateFormat="h:mm aa"
                            placeholderText="10:00 AM"
                        />
                    </div>
                </div>

                <div className={Style.fieldGroup}>
                    <P noDefaultMargin className={Style.uppercaseLabel}>
                        New note
                    </P>
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className={Style.textarea}
                        placeholder="Reason for postponing SLA…"
                    />
                </div>

                <Section noDefaultPadding className={Style.actionsCentered}>
                    <Button onClick={handleSubmit} disabled={!canSubmit}>
                        {isPending ? 'Updating…' : 'Update SLA Timer'}
                    </Button>
                </Section>
            </div>
        </Modal>
    );
};

export default PostponeSlaModal;
